import type { ComponentChildren } from 'preact'
import { Trash2, Upload } from 'lucide-preact'
import type { BrandAssetBackground, BrandAssetType, KnowledgeBaseFileViewDto } from '@/types/knowledgeBase'
import { SegmentedToggle } from '@/components/common/SegmentedToggle'
import { fileKind } from '@/components/console/knowledgeBase/brandGuidelinesModel'
import { formatBytes } from '@/utils/fileSize'
import {
  BACKGROUNDS, MARK_SLOTS, SOCIAL_SLOT, markRow, slotPhrase, type MarkCell, type SlotInfo,
} from './brandLogos'
import { H2, LEDE, QUIET_BUTTON, SECTION, SWATCH_FRAME } from './brandGuidelinesStyles'

/** The dark ground every dark version is previewed on: fixed, so it shows the same way in either app theme. */
export const DARK_GROUND = '#0F172A'

interface Props {
  files: readonly KnowledgeBaseFileViewDto[]
  /** The brand's own page background, which every light version is previewed on. */
  lightGround: string
  canChange: boolean
  pending: boolean
  /** The file whose removal is being asked about. */
  askingRemove: string | null
  onAskRemove: (fileId: string | null) => void
  onRemove: (file: KnowledgeBaseFileViewDto) => void
  /** Chooses a file for a slot: a mark's version for one background, or with null one marked for neither. */
  onUpload: (type: BrandAssetType, background: BrandAssetBackground | null) => void
  onMark: (file: KnowledgeBaseFileViewDto, background: BrandAssetBackground | null) => void
}

type PreviewGround = BrandAssetBackground | 'Either'

/**
 * Brand Guidelines' logos (ORB-968): each mark in a version for light backgrounds and one for dark ones, each previewed on
 * the ground it is made for. A file marked for neither shows once, on both grounds, until a person marks it.
 */
export function BrandLogoRows(props: Props) {
  const { files } = props
  const social = files.find(file => file.AssetType === SOCIAL_SLOT.type) ?? null

  return (
    <section aria-labelledby="bg-logos-h" className={SECTION}>
      <div className="flex flex-col gap-1.5">
        <h2 id="bg-logos-h" className={H2}>Logos</h2>
        <p className={LEDE}>
          Each mark can have a version for light backgrounds and one for dark backgrounds. Emails and landing pages use the one
          that matches the background behind it, and a file not marked either way is used on both.
        </p>
        <p id="bg-logos-note" className="max-w-[72ch] text-sm text-slate-500 dark:text-slate-400">
          Replacing or removing a file changes what Webster builds next. Pages and emails already built keep the file they were
          built with.
        </p>
      </div>
      <ul aria-label="Logo files" className="flex flex-col">
        {MARK_SLOTS.map((slot, index) => {
          const row = markRow(files, slot.type)
          return (
            <SlotRow key={slot.type} slot={slot} first={index === 0}>
              {row.kind === 'either' ? (
                <EitherVersion {...props} slot={slot} file={row.file} />
              ) : (
                <>
                  {BACKGROUNDS.map(background => (
                    <Version key={background} {...props} slot={slot} background={background} cell={row.cells[background]} />
                  ))}
                  {row.unused.map(file => <UnusedFile key={file.FileId} {...props} slot={slot} file={file} />)}
                </>
              )}
            </SlotRow>
          )
        })}
        <SlotRow slot={SOCIAL_SLOT} first={false}>
          <div className="md:col-span-2 min-w-0 flex flex-col gap-2.5">
            <h4 className="text-xs font-medium text-slate-500 dark:text-slate-400">One version: it carries its own background</h4>
            <Preview ground="Either" file={social} lightGround={props.lightGround} neutral emptyText="No social image" />
            <FileLine {...props} slot={SOCIAL_SLOT} file={social} background={null} />
          </div>
        </SlotRow>
      </ul>
    </section>
  )
}

function SlotRow({ slot, first, children }: { slot: SlotInfo; first: boolean; children: ComponentChildren }) {
  return (
    <li className={`grid grid-cols-1 md:grid-cols-[180px_minmax(0,1fr)_minmax(0,1fr)] gap-x-7 gap-y-5 ${first ? 'pb-8' : 'py-8 border-t border-slate-200 dark:border-slate-700'}`}>
      <div className="min-w-0 flex flex-col gap-1 md:pt-7">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{slot.label}</h3>
        <p className="text-[13px] leading-[19px] text-slate-500 dark:text-slate-400">{slot.usage}</p>
      </div>
      {children}
    </li>
  )
}

type VersionProps = Props & { slot: SlotInfo }

/** One background's column: the version made for it, the unmarked file used in its place, or nothing yet. */
function Version(props: VersionProps & { background: BrandAssetBackground; cell: MarkCell | null }) {
  const { slot, background, cell } = props
  const unmarked = cell !== null && !cell.marked
  return (
    <div className="min-w-0 flex flex-col gap-2.5">
      <h4 className="text-xs font-medium text-slate-500 dark:text-slate-400">For {background.toLowerCase()} backgrounds</h4>
      <Preview ground={background} file={cell?.file ?? null} lightGround={props.lightGround} emptyText={`No version for ${background.toLowerCase()} backgrounds`} />
      {unmarked ? (
        <UnmarkedLine {...props} file={cell.file} background={background} />
      ) : (
        <FileLine {...props} slot={slot} file={cell?.file ?? null} background={background} />
      )}
    </div>
  )
}

/** A file marked for neither background, and nothing marked beside it: shown once, on both grounds. */
function EitherVersion(props: VersionProps & { file: KnowledgeBaseFileViewDto }) {
  const { slot, file, canChange, pending } = props
  return (
    <div className="md:col-span-2 min-w-0 flex flex-col gap-2.5">
      <h4 className="text-xs font-medium text-slate-500 dark:text-slate-400">Either background</h4>
      {/* A border, not the frame's inset ring, which the two grounds would paint over. */}
      <div aria-hidden="true" className="h-[116px] grid grid-cols-2 overflow-hidden rounded-lg border border-slate-900/10 dark:border-slate-50/40">
        <GroundHalf ground="Light" lightGround={props.lightGround}><Image file={file} /></GroundHalf>
        <GroundHalf ground="Dark" lightGround={props.lightGround}><Image file={file} /></GroundHalf>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
        <FileName file={file} note={canChange ? 'not marked yet' : 'not marked, so it is used on both'} />
        {canChange && (
          <div className="flex flex-wrap items-center gap-2.5">
            <span aria-hidden="true" className="text-xs font-medium text-slate-500 dark:text-slate-400">For</span>
            <SegmentedToggle<'Either' | BrandAssetBackground>
              ariaLabel={`Which backgrounds ${file.FileName} is for`}
              value="Either"
              onChange={choice => { if (!pending && choice !== 'Either') props.onMark(file, choice) }}
              options={[
                { value: 'Either', label: 'Either background' },
                { value: 'Light', label: 'Light backgrounds' },
                { value: 'Dark', label: 'Dark backgrounds' },
              ]}
            />
          </div>
        )}
        <Actions {...props} slot={slot} file={file} background={null} />
      </div>
    </div>
  )
}

/** An unmarked file used in a column with no version of its own. Marking it moves it into that column's slot. */
function UnmarkedLine(props: VersionProps & { file: KnowledgeBaseFileViewDto; background: BrandAssetBackground }) {
  const { slot, file, background, canChange, pending } = props
  return (
    <div className="flex flex-col gap-2">
      <FileName file={file} note="not marked, so it is used here" />
      {canChange && (
        <div className="flex flex-wrap items-center gap-0.5 -ml-1.5">
          <button type="button" className={QUIET_BUTTON} disabled={pending} onClick={() => props.onMark(file, background)}>
            Mark it for {background.toLowerCase()} backgrounds
          </button>
          <Actions {...props} slot={slot} file={file} background={null} />
        </div>
      )}
    </div>
  )
}

/** An unmarked file both backgrounds have their own version over, so nothing uses it. */
function UnusedFile(props: VersionProps & { file: KnowledgeBaseFileViewDto }) {
  const { slot, file } = props
  return (
    <div className="md:col-start-2 md:col-span-2 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500 dark:text-slate-400">
      <span className="min-w-0">
        <span className="font-mono text-slate-600 dark:text-slate-300">{file.FileName}</span> is marked for neither background,
        and both have their own version, so nothing uses it.
      </span>
      <Actions {...props} slot={slot} file={file} background={null} removeOnly />
    </div>
  )
}

/** A filled slot's file and its actions, or an empty slot's Upload. */
function FileLine(props: VersionProps & { file: KnowledgeBaseFileViewDto | null; background: BrandAssetBackground | null }) {
  const { slot, file, background, canChange, pending } = props
  if (file === null) {
    return (
      <div className="flex items-center justify-between gap-3 min-h-[34px]">
        <span className="text-xs text-slate-500 dark:text-slate-400">None uploaded</span>
        {canChange && (
          <button type="button" className={`${QUIET_BUTTON} -mr-1.5`} disabled={pending}
            aria-label={`Upload ${slotPhrase(slot, 'a', background)}`} onClick={() => props.onUpload(slot.type, background)}>
            <Upload className="w-3 h-3" aria-hidden="true" />Upload
          </button>
        )}
      </div>
    )
  }
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <FileName file={file} />
      <Actions {...props} slot={slot} file={file} background={background} />
    </div>
  )
}

function FileName({ file, note }: { file: KnowledgeBaseFileViewDto; note?: string }) {
  return (
    <div className="min-w-0 flex flex-col gap-0.5">
      <a href={file.DownloadUrl ?? undefined} target="_blank" rel="noopener noreferrer"
        className="font-mono text-xs text-slate-600 dark:text-slate-300 truncate hover:underline">{file.FileName}</a>
      <span className="text-xs text-slate-500 dark:text-slate-400">
        {[fileKind(file.ContentType, file.FileName), formatBytes(file.FileSizeBytes), note].filter(Boolean).join(' · ')}
      </span>
    </div>
  )
}

/** Replace and Remove, with Remove asked first. A replacement keeps the background of the slot it fills. */
function Actions(props: VersionProps & { file: KnowledgeBaseFileViewDto; background: BrandAssetBackground | null; removeOnly?: boolean }) {
  const { slot, file, background, canChange, pending, askingRemove, removeOnly } = props
  if (!canChange) return null
  // The file's own slot: a version keeps its background, and an unmarked file stays unmarked.
  const named = slotPhrase(slot, 'the', file.Background ? background : null)
  if (askingRemove === file.FileId) {
    return (
      <div className="flex flex-col gap-1.5">
        <p className="text-xs text-slate-700 dark:text-slate-300">Remove {named}? What is already built keeps it.</p>
        <div className="flex items-center gap-1 -ml-1.5">
          <button type="button" className={`${QUIET_BUTTON} text-red-600 dark:text-red-400`} disabled={pending}
            onClick={() => props.onRemove(file)}>Remove</button>
          <button type="button" className={QUIET_BUTTON} onClick={() => props.onAskRemove(null)}>Keep</button>
        </div>
      </div>
    )
  }
  return (
    <div className="flex-none flex items-center gap-0.5 -mr-1.5">
      {!removeOnly && (
        <button type="button" className={QUIET_BUTTON} disabled={pending} aria-describedby="bg-logos-note"
          aria-label={`Replace ${named}`} onClick={() => props.onUpload(slot.type, file.Background ? background : null)}>
          <Upload className="w-3 h-3" aria-hidden="true" />Replace
        </button>
      )}
      <button type="button" className={QUIET_BUTTON} disabled={pending} aria-label={`Remove ${named}`}
        onClick={() => props.onAskRemove(file.FileId)}>
        <Trash2 className="w-3 h-3" aria-hidden="true" />Remove
      </button>
    </div>
  )
}

function groundColor(ground: BrandAssetBackground, lightGround: string): string {
  return ground === 'Light' ? lightGround : DARK_GROUND
}

function GroundHalf({ ground, lightGround, children }: { ground: BrandAssetBackground; lightGround: string; children: ComponentChildren }) {
  return (
    <span className="min-w-0 p-3 flex items-center justify-center overflow-hidden" data-ground={ground}
      style={{ background: groundColor(ground, lightGround) }}>
      {children}
    </span>
  )
}

function Image({ file }: { file: KnowledgeBaseFileViewDto }) {
  const src = file.ContentType.startsWith('image/') && file.DownloadUrl ? file.DownloadUrl : null
  return src ? <img src={src} alt="" className="max-w-full max-h-full object-contain" /> : null
}

/** A version on the ground it is made for, or an empty slot drawn on that ground. */
function Preview({ ground, file, lightGround, emptyText, neutral = false }: {
  ground: PreviewGround
  file: KnowledgeBaseFileViewDto | null
  lightGround: string
  emptyText: string
  /** The social image carries its own background, so it sits on the page's own sunken ground. */
  neutral?: boolean
}) {
  const background = neutral || ground === 'Either' ? undefined : groundColor(ground, lightGround)
  if (file === null) {
    const onDark = ground === 'Dark'
    return (
      <div aria-hidden="true" style={background ? { background } : undefined}
        className={`h-[116px] rounded-lg border-[1.5px] border-dashed flex flex-col items-center justify-center gap-2 text-[13px] ${
          onDark ? 'border-white/40 text-white/80'
            : neutral ? 'border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900'
              : 'border-slate-400/70 text-slate-600'}`}>
        <Upload className="w-[18px] h-[18px]" />
        <span>{emptyText}</span>
      </div>
    )
  }
  return (
    <div aria-hidden="true" data-ground={neutral ? 'Neutral' : ground} style={background ? { background } : undefined}
      className={`h-[116px] p-3 flex items-center justify-center overflow-hidden ${neutral ? 'rounded-lg bg-slate-50 dark:bg-slate-900 ring-1 ring-inset ring-slate-200 dark:ring-slate-700' : SWATCH_FRAME}`}>
      <Image file={file} />
    </div>
  )
}
